import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import {
  getMyPendingIssuancesApi,
  acceptIssuanceApi,
  rejectIssuanceApi,
} from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { rejectionReasonOptions } from '../../constants';

const PendingAcceptanceModal = () => {
  const [visible, setVisible] = useState(false);
  const [pending, setPending] = useState([]);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState(null);
  const [rejectDetail, setRejectDetail] = useState('');

  useEffect(() => {
    checkPending();
  }, []);

  const checkPending = () => {
    getMyPendingIssuancesApi({}).then(response => {
      const items = response.data?.data || [];
      setPending(items);
      if (items.length > 0) setVisible(true);
    }).catch(() => {});
  };

  const removeFromList = (id) => {
    setPending(prev => {
      const next = prev.filter(p => p.id !== id);
      if (next.length === 0) setVisible(false);
      return next;
    });
  };

  const handleAccept = (id) => {
    setActionLoadingId(id);
    acceptIssuanceApi(id).then(() => {
      toast.success('Uniform accepted', { position: "top-right", autoClose: 2500, transition: Bounce });
      removeFromList(id);
    }).catch(e => {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Failed to accept';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    }).finally(() => setActionLoadingId(null));
  };

  const openReject = (id) => {
    setRejectingId(id);
    setRejectReason(null);
    setRejectDetail('');
  };

  const closeReject = () => {
    setRejectingId(null);
    setRejectReason(null);
    setRejectDetail('');
  };

  const submitReject = () => {
    if (!rejectReason) {
      toast.error('Please select a reason', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setActionLoadingId(rejectingId);
    rejectIssuanceApi(rejectingId, {
      reason: rejectReason.value,
      reason_detail: rejectDetail,
    }).then(() => {
      toast.success('Uniform rejected', { position: "top-right", autoClose: 2500, transition: Bounce });
      const idToRemove = rejectingId;
      closeReject();
      removeFromList(idToRemove);
    }).catch(e => {
      const msg = e.response?.data?.error || e.response?.data?.detail || 'Failed to reject';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    }).finally(() => setActionLoadingId(null));
  };

  const handleSkip = () => setVisible(false);

  if (!visible || pending.length === 0) return null;

  return (
    <>
      <Modal
        title={
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Pending Uniform Issuances</div>
            <div style={{ fontSize: 13, fontWeight: 400, color: '#6B7280', marginTop: 4 }}>
              {pending.length} uniform{pending.length > 1 ? 's' : ''} waiting for your confirmation
            </div>
          </div>
        }
        open={visible}
        onCancel={handleSkip}
        maskClosable={false}
        keyboard={false}
        width={640}
        centered
        zIndex={1200}
        bodyStyle={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 8px 0' }}
        footer={[
          <button key="skip" type="button" className="btn btn-light"
            onClick={handleSkip} disabled={actionLoadingId !== null}>
            Skip for now
          </button>
        ]}
      >
        {pending.map(item => (
          <div key={item.id} className="p-3 mb-2"
            style={{ background: '#F9FAFB', borderRadius: 12, border: '1px solid #F1F2F4' }}>
            <div className="d-flex align-items-start gap-3">
              {item.uniform_item_image ? (
                <img src={item.uniform_item_image} alt={item.uniform_item_name}
                  style={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 8, background: '#fff', flexShrink: 0 }} />
              ) : (
                <div style={{
                  width: 56, height: 56, borderRadius: 8, background: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  color: '#9CA3AF', border: '1px solid #F1F2F4'
                }}>
                  <i className="fa fa-tshirt" />
                </div>
              )}

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>
                  {item.uniform_item_name}
                </div>
                <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
                  {item.size_detail ? `${item.size_detail.code}` : 'No size'}
                  {' · '}Qty {item.quantity}
                  {' · '}{formatDateHandler(item.issuance_date)}
                </div>
                {item.warehouse_name && (
                  <div className="text-muted text-truncate" style={{ fontSize: 12 }}>
                    From {item.warehouse_name}
                  </div>
                )}
              </div>
            </div>

            <div className="d-flex gap-2 mt-3">
              <Button
                label="Accept"
                icon="fa fa-check"
                className="btn btn-success btn-sm flex-grow-1"
                loading={actionLoadingId === item.id && rejectingId !== item.id}
                disabled={actionLoadingId !== null}
                onClick={() => handleAccept(item.id)}
              />
              <Button
                label="Reject"
                icon="fa fa-times"
                className="btn btn-outline-danger btn-sm flex-grow-1"
                disabled={actionLoadingId !== null}
                onClick={() => openReject(item.id)}
              />
            </div>
          </div>
        ))}
      </Modal>

      <Modal
        title="Reject Uniform"
        open={rejectingId !== null}
        onCancel={closeReject}
        width={460}
        centered
        zIndex={1300}
        footer={[
          <button key="cancel" type="button" className="btn btn-light"
            onClick={closeReject} disabled={actionLoadingId === rejectingId}>
            Cancel
          </button>,
          <Button
            key="submit"
            label="Submit Rejection"
            className="btn btn-danger ms-2"
            loading={actionLoadingId === rejectingId}
            onClick={submitReject}
          />
        ]}
      >
        <div className="mb-3">
          <label className="col-form-label">Reason <span className="text-danger">*</span></label>
          <FilterSelect
            isClearable
            onChange={setRejectReason}
            optionLabel="label"
            optionValue="value"
            options={rejectionReasonOptions}
            value={rejectReason}
            placeholder="Select Reason"
            isLoading={false}
          />
        </div>
        <div className="mb-1">
          <label className="col-form-label">Detail <small className="text-muted">(optional)</small></label>
          <textarea className="form-control" rows={3} value={rejectDetail}
            onChange={(e) => setRejectDetail(e.target.value)}
            placeholder="Additional details..." />
        </div>
      </Modal>
    </>
  );
};

export default PendingAcceptanceModal;
